'use client';
import { Fragment } from 'react';
import { useCmsClientContext } from '../../../../utils/cmsPageContext';
import { getClientTranslations } from '../../../../utils/translations/getClientTranslations';
import { FeLink } from '../../../FeLink';
import { TRANSLATIONS } from '../../i18n';
import { AioModuleTracking, sendAioNavigate } from '../../tracking';
import { AioRelevantArticleLink } from '../../types';
import { resolveRelatedLink } from './utils';

type RelatedReadingAioModuleProps = {
  links: AioRelevantArticleLink[];
} & AioModuleTracking;

/**
 * "Related Reading" line of the AIO module (CMS-1937) — the label followed by
 * up to three comma-separated links, inline rather than as cards.
 *
 * The label is fixed copy (a Figma variable, not an editor field), so it comes
 * from i18n. Links resolve in `utils.ts`; anything that resolves to no URL or
 * no text is dropped, and the whole line disappears when nothing is left.
 */
export const RelatedReadingAioModule = ({
  links,
  ...tracking
}: RelatedReadingAioModuleProps): JSX.Element | null => {
  const { brand, lang, langPrefix } = useCmsClientContext();
  const t = getClientTranslations(TRANSLATIONS, { brand, lang });

  const resolved = links.flatMap((link) => {
    const item = resolveRelatedLink(link, { brand, langPrefix });

    return item ? [item] : [];
  });

  if (!resolved.length) {
    return null;
  }

  return (
    <p
      data-component="aio-module-relevant-articles"
      className="mhg-alpha-typography-button-small text-text/secondary-lighter"
    >
      <span className="mhg-alpha-bullet-2">
        {t.aio_module_related_reading}{' '}
      </span>
      {resolved.map((item, index) => (
        <Fragment key={item.id}>
          {index > 0 && ', '}
          {/* External destinations open in a new tab, like the module's two
              other outbound links - an advertiser link should not navigate the
              reader out of the article. */}
          <FeLink
            href={item.url}
            className="text-text/link underline"
            target={item.isExternal ? '_blank' : undefined}
            rel={item.isExternal ? 'noopener noreferrer' : undefined}
            onClick={() =>
              // #2539
              sendAioNavigate(tracking, {
                componentType: 'text-link',
                title: `Related Reading_${item.label}`,
                action: 'click',
                index,
              })
            }
          >
            {item.label}
          </FeLink>
        </Fragment>
      ))}
    </p>
  );
};
